import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { reportClientError } from '../utils/clientLogger';
import {
  SERVICE_OPTIONS,
  CONSULTATION_OPTIONS,
  WORK_OPTIONS,
  SOCIAL_ITEMS,
  TIME_OPTIONS,
  INITIAL_FORM_DATA
} from '../data/questionarioCasasConfig';
import { QuestionarioCasasSuccess } from '../components/questionarios/casas/QuestionarioCasasSuccess';
import { QuestionarioCasasProgress } from '../components/questionarios/casas/QuestionarioCasasProgress';
import { QuestionarioCasasStepOne } from '../components/questionarios/casas/QuestionarioCasasStepOne';
import { QuestionarioCasasStepTwo } from '../components/questionarios/casas/QuestionarioCasasStepTwo';
import { QuestionarioCasasStepThree } from '../components/questionarios/casas/QuestionarioCasasStepThree';
import { QuestionarioCasasStepFour } from '../components/questionarios/casas/QuestionarioCasasStepFour';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;




const optionLabel = (options, value) => options.find((item) => item.value === value)?.label || value || 'Não informado';
const formatFileSize = (size) => `${(size / 1024 / 1024).toFixed(size >= 1024 * 1024 ? 1 : 2)} MB`;

const QuestionarioCasas = () => {
  const { codigo } = useParams();
  const [searchParams] = useSearchParams();
  const vendedorCodigo = codigo || searchParams.get('v') || localStorage.getItem('vendedor_codigo') || '';

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (vendedorCodigo) {
      localStorage.setItem('vendedor_codigo', vendedorCodigo);
    }
  }, [vendedorCodigo]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((previous) => ({
      ...previous,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSocialItemToggle = (item) => {
    setFormData((previous) => {
      const currentItems = previous.socialItems;
      const hasItem = currentItems.includes(item);

      if (item === 'Nenhum dos itens acima') {
        return {
          ...previous,
          socialItems: hasItem ? [] : [item],
        };
      }

      const filtered = currentItems.filter((entry) => entry !== 'Nenhum dos itens acima');
      return {
        ...previous,
        socialItems: hasItem ? filtered.filter((entry) => entry !== item) : [...filtered, item],
      };
    });
  };

  const handleFileSelection = (event) => {
    setSelectedFiles(Array.from(event.target.files || []));
  };

  const clearSelectedFiles = () => {
    setSelectedFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const isStepValid = () => {
    if (step === 1) {
      return Boolean(formData.name && formData.email && formData.phone && formData.bestContactTime);
    }

    if (step === 2) {
      return Boolean(
        formData.serviceCategory &&
          formData.consultationType &&
          formData.workType &&
          formData.hasOtherRealEstateConsultation,
      );
    }

    if (step === 3) {
      if (formData.serviceCategory === 'casas') {
        return Boolean(formData.propertyGoal && formData.budgetRange && formData.preferredArea);
      }

      if (formData.serviceCategory === 'reforma') {
        return Boolean(formData.reformType && formData.reformPropertyType && formData.reformTimeline);
      }

      if (formData.serviceCategory === 'painel_solar') {
        return Boolean(formData.solarPropertyType && formData.monthlyEnergyBill && formData.solarGoal);
      }

      return false;
    }

    return Boolean(
      formData.attendancePreference &&
        formData.firstPreferredDate &&
        formData.firstPreferredTime &&
        formData.privacyAccepted,
    );
  };

  const nextStep = () => setStep((current) => Math.min(current + 1, 4));
  const prevStep = () => setStep((current) => Math.max(current - 1, 1));

  const buildMessage = (uploadedFiles = []) => `
=== QUESTIONÁRIO RH HOLDINGS - CASAS / REFORMA / PAINEL SOLAR ===

ORIGEM:
- Código do vendedor: ${vendedorCodigo || 'Não informado'}

DADOS PESSOAIS:
- Nome: ${formData.name}
- Sexo: ${formData.gender || 'Não informado'}
- Faixa etária: ${formData.ageRange || 'Não informado'}
- Endereço: ${formData.address || 'Não informado'}
- Celular: ${formData.phone}
- Telefone residencial: ${formData.homePhone || 'Não informado'}
- E-mail: ${formData.email}
- Melhor horário para contato: ${formData.bestContactTime}

PERFIL E CONSULTA:
- Serviço de interesse: ${optionLabel(SERVICE_OPTIONS, formData.serviceCategory)}
- Tipo de consulta: ${optionLabel(CONSULTATION_OPTIONS, formData.consultationType)}
- Já consultou outra imobiliária/empresa?: ${formData.hasOtherRealEstateConsultation === 'sim' ? 'Sim' : 'Não'}
- Trabalho: ${optionLabel(WORK_OPTIONS, formData.workType)}
- Itens que possui: ${formData.socialItems.length > 0 ? formData.socialItems.join(', ') : 'Nenhum informado'}

DETALHES DO SERVIÇO:
- Casas | Objetivo: ${formData.propertyGoal || 'N/A'}
- Casas | Área de interesse: ${formData.preferredArea || 'N/A'}
- Casas | Faixa de orçamento: ${formData.budgetRange || 'N/A'}
- Casas | Situação de financiamento: ${formData.financingStatus || 'N/A'}
- Reforma | Tipo de reforma: ${formData.reformType || 'N/A'}
- Reforma | Tipo de imóvel: ${formData.reformPropertyType || 'N/A'}
- Reforma | Prazo: ${formData.reformTimeline || 'N/A'}
- Solar | Tipo de imóvel: ${formData.solarPropertyType || 'N/A'}
- Solar | Conta média de energia: ${formData.monthlyEnergyBill || 'N/A'}
- Solar | Objetivo principal: ${formData.solarGoal || 'N/A'}
- Solar | Interesse em bateria: ${formData.hasBatteryInterest || 'N/A'}

ATENDIMENTO:
- Preferência de atendimento: ${formData.attendancePreference}
- 1ª data preferida: ${formData.firstPreferredDate}
- 1º horário preferido: ${formData.firstPreferredTime}
- 2ª data preferida: ${formData.secondPreferredDate || 'Não informado'}
- 2º horário preferido: ${formData.secondPreferredTime || 'Não informado'}

OBSERVAÇÕES:
${formData.notes || 'Nenhuma'}

ARQUIVOS ENVIADOS:
${uploadedFiles.length > 0 ? uploadedFiles.map((file) => `- ${file.original_name} (${file.url})`).join('\n') : 'Nenhum arquivo anexado'}
`;

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!isStepValid()) {
      return;
    }

    setSubmitting(true);
    try {
      let uploadedFiles = [];

      if (selectedFiles.length > 0) {
        const uploadData = new FormData();
        selectedFiles.forEach((file) => {
          uploadData.append('files', file);
        });

        const uploadResponse = await axios.post(`${API}/upload`, uploadData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        uploadedFiles = uploadResponse.data.files || [];
      }

      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: buildMessage(uploadedFiles),
        uploaded_files: uploadedFiles,
      });

      clearSelectedFiles();
      setSubmitted(true);
    } catch (error) {
      reportClientError('Error submitting questionnaire:', error);
      const errorMessage = error.response?.data?.detail || 'Erro ao enviar questionário. Tente novamente.';
      alert(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return <QuestionarioCasasSuccess />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-cyan-50" data-testid="questionario-casas-page">
      <div className="bg-white shadow-sm" data-testid="questionario-casas-header">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-gray-600 hover:text-gray-900" data-testid="questionario-casas-back-link">← Voltar ao site</Link>
          <h1 className="text-xl font-bold text-blue-700" data-testid="questionario-casas-brand">RH Consulting Holdings</h1>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <QuestionarioCasasProgress step={step} />

        <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8" data-testid="questionario-casas-form-card">
          <div className="mb-6" data-testid="questionario-casas-intro">
            <h2 className="text-2xl font-bold text-gray-900 mb-2" data-testid="questionario-casas-title">Questionário de Casas, Reforma e Painel Solar</h2>
            <p className="text-gray-600" data-testid="questionario-casas-subtitle">Modelo atualizado com base no mercado e adaptado para RH Holdings.</p>
          </div>

          <form onSubmit={handleSubmit} data-testid="questionario-casas-form">
            {step === 1 && <QuestionarioCasasStepOne formData={formData} handleChange={handleChange} />}
            {step === 2 && (
              <QuestionarioCasasStepTwo
                formData={formData}
                handleChange={handleChange}
                handleSocialItemToggle={handleSocialItemToggle}
                serviceOptions={SERVICE_OPTIONS}
                consultationOptions={CONSULTATION_OPTIONS}
                workOptions={WORK_OPTIONS}
                socialItems={SOCIAL_ITEMS}
              />
            )}
            {step === 3 && <QuestionarioCasasStepThree formData={formData} handleChange={handleChange} />}
            {step === 4 && (
              <QuestionarioCasasStepFour
                formData={formData}
                handleChange={handleChange}
                timeOptions={TIME_OPTIONS}
                selectedFiles={selectedFiles}
                clearSelectedFiles={clearSelectedFiles}
                fileInputRef={fileInputRef}
                handleFileSelection={handleFileSelection}
                formatFileSize={formatFileSize}
              />
            )}

            <div className="flex gap-3 mt-8" data-testid="questionario-casas-navigation-buttons">
              {step > 1 && (
                <button type="button" onClick={prevStep} className="flex-1 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition" data-testid="questionario-casas-prev-button">
                  ← Voltar
                </button>
              )}

              {step < 4 ? (
                <button type="button" onClick={nextStep} disabled={!isStepValid()} className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed" data-testid="questionario-casas-next-button">
                  Próximo →
                </button>
              ) : (
                <button type="submit" disabled={submitting || !isStepValid()} className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed" data-testid="questionario-casas-submit-button">
                  {submitting ? 'Enviando...' : '✓ Enviar questionário'}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default QuestionarioCasas;
